// Workspace board — status columns; drag a card to change its status (Undo on every move).
import { useState } from "react";
import { toast } from "sonner";
import { workspaceApi, emitTasksChanged, dueInfo, teamColor } from "../_shared";
import { completeWithUndo, isRoutine } from "./TaskBits";

const RECENT = 7 * 86_400_000;
const PRIO = { urgent: "text-rose-300", high: "text-orange-300", medium: "text-slate-300", low: "text-slate-500" };

function Card({ t, userMap, teamMap, onOpen, onDragStart }) {
  const d = dueInfo(t);
  const team = teamMap[t.team_id];
  return (
    <div draggable onDragStart={(e) => onDragStart(e, t)} onClick={() => onOpen(t)} data-testid={`board-card-${t.id}`}
      className="rounded-lg border border-white/10 bg-white/[0.03] hover:border-white/25 hover:bg-white/[0.05] p-2.5 cursor-grab active:cursor-grabbing transition-colors">
      <div className="text-[12.5px] text-white leading-snug"><span className="font-mono text-[10.5px] text-slate-500 mr-1">{t.ref}</span>{t.title}</div>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1.5 text-[10.5px]">
        {team && <span className="inline-flex items-center gap-1 text-slate-300"><span className="w-1.5 h-1.5 rounded-full" style={{ background: teamColor(team) }} />{team.name}</span>}
        <span className="text-slate-400">{userMap[t.assignee_id]?.name || (t.assign_mode === "each" ? "Each member" : "Unassigned")}</span>
        {t.priority && <span className={`capitalize ${PRIO[t.priority] || ""}`}>{t.priority}</span>}
        {t.due_date && <span className={d.tone === "red" ? "text-rose-300" : "text-slate-400"}>{d.text}</span>}
      </div>
    </div>
  );
}

export default function TaskBoard({ tasks, statuses, userMap, teamMap, onOpen, onChanged }) {
  const [over, setOver] = useState(null);
  const [moved, setMoved] = useState({});
  const cols = statuses.length ? statuses : [{ key: "todo", label: "To do" }, { key: "in_progress", label: "In progress" }, { key: "done", label: "Done" }];
  const status = (t) => moved[t.id] || t.status;
  const visible = tasks.filter((t) => !["done", "cancelled"].includes(status(t)) || moved[t.id] || Date.now() - new Date(t.completed_at || t.updated_at) < RECENT);
  const onDragStart = (e, t) => { e.dataTransfer.setData("text/plain", t.id); e.dataTransfer.effectAllowed = "move"; };
  const drop = async (e, key) => {
    e.preventDefault(); setOver(null);
    const t = tasks.find((x) => x.id === e.dataTransfer.getData("text/plain"));
    if (!t || status(t) === key) return;
    const prev = status(t);
    setMoved((m) => ({ ...m, [t.id]: key }));
    const revert = () => setMoved((m) => { const n = { ...m }; delete n[t.id]; return n; });
    if (key === "done") {
      const ok = await completeWithUndo(t, onChanged, revert);
      if (!ok) { revert(); onOpen(t); }
      else if (isRoutine(t) || (t.team_id && t.assign_mode === "each")) revert();
      return;
    }
    try { await workspaceApi.patchTask(t.id, { status: key }); }
    catch (err) { revert(); toast.error("Couldn't move", { description: err?.response?.data?.detail || "" }); return; }
    emitTasksChanged(); onChanged?.();
    const label = cols.find((c) => c.key === key)?.label || key;
    toast.success(`${t.ref || ""} moved to ${label}`.trim(), { duration: 10000, action: { label: "Undo", onClick: async () => {
      await workspaceApi.patchTask(t.id, { status: prev }); setMoved((m) => ({ ...m, [t.id]: prev })); emitTasksChanged(); onChanged?.();
    } } });
  };
  return (
    <div className="flex gap-3 overflow-x-auto pb-2" data-testid="task-board">
      {cols.map((c) => {
        const items = visible.filter((t) => status(t) === c.key);
        return (
          <div key={c.key} onDragOver={(e) => { e.preventDefault(); setOver(c.key); }} onDragLeave={() => setOver((o) => (o === c.key ? null : o))} onDrop={(e) => drop(e, c.key)}
            className={`w-[270px] shrink-0 rounded-xl border p-2 transition-colors ${over === c.key ? "border-amber-400/60 bg-amber-400/[0.06]" : "border-white/[0.07] bg-white/[0.015]"}`}
            data-testid={`board-col-${c.key}`}>
            <div className="flex items-center justify-between px-1 pb-2">
              <span className="text-[10.5px] uppercase tracking-widest font-mono text-slate-300">{c.label}</span>
              <span className="text-[10.5px] font-mono text-slate-500" data-testid={`board-count-${c.key}`}>{items.length}</span>
            </div>
            <div className="space-y-2 min-h-[80px] max-h-[65vh] overflow-y-auto">
              {items.map((t) => <Card key={t.id} t={t} userMap={userMap} teamMap={teamMap} onOpen={onOpen} onDragStart={onDragStart} />)}
              {!items.length && <div className="text-[11px] text-slate-600 text-center py-6">Drop tasks here</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}
